import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "../components/Layout";
import BackToProjectBanner from "../components/BackToProjectBanner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import DatePickerField from "../components/DatePickerField";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Loader2, Trash2, Edit, ClipboardCheck, Download, Link } from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";
import { useData } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import PhotoUpload, { BeforeAfterComparison } from "../components/PhotoUpload";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CONDITION_COLORS = {
  good: "bg-emerald-100 text-emerald-700",
  fair: "bg-amber-100 text-amber-700",
  poor: "bg-orange-100 text-orange-700",
  damaged: "bg-red-100 text-red-700",
  na: "bg-slate-100 text-slate-500",
};

const REPORT_TYPES = [
  { value: "general", label: "General Inspection" },
  { value: "roof", label: "Roof Inspection" },
  { value: "siding", label: "Siding Inspection" },
  { value: "fence", label: "Fence Inspection" },
  { value: "gutter", label: "Gutter Inspection" },
  { value: "storm_damage", label: "Storm Damage" },
];

const ROOF_COMPONENTS = [
  { area: "Shingles / Roofing Material", condition: "good", notes: "" },
  { area: "Ridge Caps", condition: "good", notes: "" },
  { area: "Flashing", condition: "good", notes: "" },
  { area: "Valleys", condition: "good", notes: "" },
  { area: "Gutters & Downspouts", condition: "good", notes: "" },
  { area: "Fascia & Drip Edge", condition: "good", notes: "" },
  { area: "Soffits & Ventilation", condition: "good", notes: "" },
  { area: "Pipe Boots & Penetrations", condition: "good", notes: "" },
  { area: "Decking / Substrate", condition: "good", notes: "" },
  { area: "Skylights / Chimneys", condition: "na", notes: "" },
];

const SIDING_COMPONENTS = [
  { area: "Siding Panels", condition: "good", notes: "" },
  { area: "Trim & Corners", condition: "good", notes: "" },
  { area: "Caulking & Sealant", condition: "good", notes: "" },
  { area: "Windows & Wraps", condition: "good", notes: "" },
  { area: "Doors & Thresholds", condition: "good", notes: "" },
  { area: "Foundation Line", condition: "good", notes: "" },
];

const FENCE_COMPONENTS = [
  { area: "Posts", condition: "good", notes: "" },
  { area: "Panels / Pickets", condition: "good", notes: "" },
  { area: "Rails", condition: "good", notes: "" },
  { area: "Gates & Hardware", condition: "good", notes: "" },
  { area: "Foundation / Ground Line", condition: "good", notes: "" },
];

const GUTTER_COMPONENTS = [
  { area: "Gutter Sections", condition: "good", notes: "" },
  { area: "Downspouts", condition: "good", notes: "" },
  { area: "Hangers & Brackets", condition: "good", notes: "" },
  { area: "End Caps & Joints", condition: "good", notes: "" },
  { area: "Splash Blocks / Extensions", condition: "good", notes: "" },
];

const STORM_COMPONENTS = [
  { area: "Roof Surface", condition: "good", notes: "" },
  { area: "Shingle Damage / Bruising", condition: "good", notes: "" },
  { area: "Gutters & Downspouts", condition: "good", notes: "" },
  { area: "Siding & Trim", condition: "good", notes: "" },
  { area: "Windows & Screens", condition: "good", notes: "" },
  { area: "Fencing", condition: "good", notes: "" },
  { area: "Soft Metals (Vents, AC)", condition: "good", notes: "" },
];

function getDefaultComponents(type) {
  switch (type) {
    case "roof": return ROOF_COMPONENTS.map(c => ({ ...c }));
    case "siding": return SIDING_COMPONENTS.map(c => ({ ...c }));
    case "fence": return FENCE_COMPONENTS.map(c => ({ ...c }));
    case "gutter": return GUTTER_COMPONENTS.map(c => ({ ...c }));
    case "storm_damage": return STORM_COMPONENTS.map(c => ({ ...c }));
    default: return [];
  }
}

const emptyForm = () => ({
  project_id: "", contact_id: "", inspector_name: "",
  inspection_date: new Date().toISOString().split("T")[0],
  property_address: "", report_type: "roof",
  items: ROOF_COMPONENTS.map(c => ({ ...c })),
  summary: "", recommendations: "", status: "draft"
});

export default function InspectionReports() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { contacts, fetchContacts, projects, fetchProjects } = useData();
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState(emptyForm());
  const [reportPhotos, setReportPhotos] = useState([]);

  useEffect(() => {
    fetchReports(); fetchContacts(); fetchProjects();
  }, [fetchContacts, fetchProjects]);

  // Auto-open create modal with project pre-filled when coming from ProjectDetail
  useEffect(() => {
    if (searchParams.get("create") === "true" && projects.length > 0) {
      const projectId = searchParams.get("project_id");
      const form = emptyForm();
      form.inspector_name = user?.name || "";
      if (projectId) {
        const project = projects.find(p => p.id === projectId);
        if (project) {
          form.project_id = projectId;
          form.contact_id = project.contact_id || "";
          form.property_address = project.project_address || "";
        }
      }
      setFormData(form);
      setShowModal(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, projects, user]);

  const fetchReports = async () => {
    try { const res = await axios.get(`${API_URL}/inspections`); setReports(res.data); }
    catch (e) { console.error(e); }
    setLoading(false);
  };

  const openCreateModal = () => {
    const form = emptyForm();
    form.inspector_name = user?.name || "";
    setFormData(form);
    setShowModal(true);
  };

  const handleReportTypeChange = (type) => {
    const existingCustomItems = formData.items.filter(item =>
      !getDefaultComponents(formData.report_type).some(dc => dc.area === item.area)
    );
    const defaultItems = getDefaultComponents(type);
    setFormData(prev => ({
      ...prev,
      report_type: type,
      items: [...defaultItems, ...existingCustomItems]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingReport) {
        await axios.put(`${API_URL}/inspections/${editingReport.id}`, formData);
        toast.success("Report updated!");
      } else {
        await axios.post(`${API_URL}/inspections`, formData);
        toast.success("Report created!");
      }
      fetchReports(); closeModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save report");
    }
    setSubmitting(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inspection report?")) return;
    try {
      await axios.delete(`${API_URL}/inspections/${id}`);
      setReports(prev => prev.filter(r => r.id !== id));
      toast.success("Moved to trash");
    } catch { toast.error("Failed to delete"); }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/inspections/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.setAttribute("download", `Inspection-${id}.pdf`);
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("PDF downloaded!");
    } catch { toast.error("Failed to generate PDF"); }
  };

  const updateItem = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === idx ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (idx) => {
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const addCustomItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { area: "Custom Item", condition: "good", notes: "" }]
    }));
  };

  const handleEdit = async (report) => {
    setEditingReport(report); setFormData(report); setShowModal(true);
    try {
      const res = await axios.get(`${API_URL}/uploads/entity/inspection/${report.id}`);
      setReportPhotos(res.data);
    } catch { setReportPhotos([]); }
  };

  const closeModal = () => {
    setShowModal(false); setEditingReport(null); setReportPhotos([]);
    setFormData(emptyForm());
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setFormData(prev => ({
      ...prev, project_id: projectId,
      contact_id: project?.contact_id || prev.contact_id,
      property_address: project?.project_address || prev.property_address
    }));
  };

  return (
    <div className="min-h-screen">
      <BackToProjectBanner />
      <Header title="Inspection Reports">
        <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6" onClick={openCreateModal} data-testid="create-inspection-btn">
          <Plus className="w-5 h-5 mr-2" />New Report
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>
        ) : reports.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map(report => (
              <Card key={report.id} data-testid={`inspection-card-${report.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{report.report_number || "Draft"}</CardTitle>
                      <p className="text-sm text-slate-500 mt-1">{report.property_address || "No address"}</p>
                    </div>
                    <Badge className={report.status === "completed" ? "bg-emerald-100 text-emerald-700" : report.status === "sent" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"}>
                      {report.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-slate-600">
                    <p>Type: {REPORT_TYPES.find(t => t.value === report.report_type)?.label || report.report_type}</p>
                    <p>Items: {report.items?.length || 0} findings</p>
                    <p>Date: {report.inspection_date || "N/A"}</p>
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(report)} data-testid={`edit-inspection-${report.id}`}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(report.id)}><Download className="w-4 h-4 mr-1" />PDF</Button>
                    <Button variant="ghost" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/inspection/${report.id}`); toast.success("Public link copied!"); }} data-testid={`share-inspection-${report.id}`}><Link className="w-4 h-4 mr-1" />Share</Button>
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 hover:bg-red-50 ml-auto" onClick={() => handleDelete(report.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <ClipboardCheck className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No inspection reports</h3>
            <p className="text-slate-500 mb-4">Create your first inspection report</p>
            <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={openCreateModal}><Plus className="w-5 h-5 mr-2" />New Report</Button>
          </div>
        )}
      </div>

      {/* CREATE / EDIT MODAL */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingReport ? "Edit Report" : "New Inspection Report"}</DialogTitle>
            <DialogDescription>Document property conditions and findings</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-5 mt-4">
            {/* Row 1: Project + Report Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Project</Label>
                <Select value={formData.project_id || "none"} onValueChange={(v) => v === "none" ? null : handleProjectSelect(v)}>
                  <SelectTrigger className="h-10" data-testid="inspection-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map(p => {
                      const contact = contacts.find(c => c.id === p.contact_id);
                      return <SelectItem key={p.id} value={p.id}>{contact?.name || "No Contact"} — {p.project_address || "No address"}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Report Type</Label>
                <Select value={formData.report_type} onValueChange={handleReportTypeChange}>
                  <SelectTrigger className="h-10" data-testid="inspection-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REPORT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Address + Inspector + Date */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Property Address</Label>
                <Input value={formData.property_address} onChange={e => setFormData({ ...formData, property_address: e.target.value })} className="h-10" data-testid="inspection-address" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Inspector</Label>
                <Input value={formData.inspector_name} onChange={e => setFormData({ ...formData, inspector_name: e.target.value })} className="h-10" data-testid="inspection-inspector" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Date</Label>
                <DatePickerField value={formData.inspection_date} onChange={(v) => setFormData({ ...formData, inspection_date: v })} testId="inspection-date" height="h-10" />
              </div>
            </div>

            {/* Component Ratings Table */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700">Component Ratings</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={addCustomItem} data-testid="add-custom-item-btn">
                  <Plus className="w-3 h-3 mr-1" />Add Item
                </Button>
              </div>

              <div className="border border-slate-200 rounded-lg overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-[1fr_120px_1fr_32px] gap-0 bg-slate-100 border-b border-slate-200 px-3 py-2">
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Component</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Condition</span>
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Notes / Description</span>
                  <span></span>
                </div>

                {/* Table Rows */}
                {formData.items.map((item, idx) => (
                  <div key={idx} className={`grid grid-cols-[1fr_120px_1fr_32px] gap-0 items-center px-3 py-1.5 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/50"} border-b border-slate-100 last:border-b-0 group`} data-testid={`inspection-item-${idx}`}>
                    {/* Component Name */}
                    <Input
                      value={item.area}
                      onChange={e => updateItem(idx, "area", e.target.value)}
                      className="h-8 border-0 bg-transparent px-0 text-sm font-medium text-slate-700 focus-visible:ring-0 focus-visible:ring-offset-0"
                      data-testid={`item-area-${idx}`}
                    />

                    {/* Condition Badge Select */}
                    <Select value={item.condition} onValueChange={v => updateItem(idx, "condition", v)}>
                      <SelectTrigger className="h-7 border-0 bg-transparent px-0 focus:ring-0 [&>span]:px-0" data-testid={`item-condition-${idx}`}>
                        <Badge className={`${CONDITION_COLORS[item.condition]} text-[11px] px-2 py-0 capitalize cursor-pointer`}>
                          {item.condition === "na" ? "N/A" : item.condition}
                        </Badge>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="good">Good</SelectItem>
                        <SelectItem value="fair">Fair</SelectItem>
                        <SelectItem value="poor">Poor</SelectItem>
                        <SelectItem value="damaged">Damaged</SelectItem>
                        <SelectItem value="na">N/A</SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Notes - click to expand */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button type="button" className="h-8 w-full px-1 text-sm text-left truncate text-slate-600 hover:bg-slate-100 rounded transition-colors" data-testid={`item-notes-${idx}`}>
                          {item.notes || <span className="text-slate-300">Add notes...</span>}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <Label className="text-xs text-slate-500 mb-1.5 block">{item.area} — Notes</Label>
                        <Textarea
                          value={item.notes}
                          onChange={e => updateItem(idx, "notes", e.target.value)}
                          placeholder="Describe condition, damage, observations..."
                          rows={4}
                          className="text-sm"
                          autoFocus
                          data-testid={`item-notes-expanded-${idx}`}
                        />
                      </PopoverContent>
                    </Popover>

                    {/* Delete */}
                    <Button
                      type="button" variant="ghost" size="sm"
                      className="h-6 w-6 p-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-opacity"
                      onClick={() => removeItem(idx)}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}

                {formData.items.length === 0 && (
                  <div className="text-center py-6 text-sm text-slate-400">
                    Select a report type to auto-populate components, or add items manually.
                  </div>
                )}
              </div>
            </div>

            {/* Summary */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Summary</Label>
              <Textarea value={formData.summary} onChange={e => setFormData({ ...formData, summary: e.target.value })} placeholder="Overall findings and observations..." rows={2} className="text-sm" data-testid="inspection-summary" />
            </div>

            {/* Recommendations */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-slate-500 uppercase tracking-wide">Recommendations</Label>
              <Textarea value={formData.recommendations} onChange={e => setFormData({ ...formData, recommendations: e.target.value })} placeholder="Recommended repairs or actions..." rows={2} className="text-sm" data-testid="inspection-recommendations" />
            </div>

            {/* Photo Upload (edit mode only) */}
            {editingReport && (
              <div className="space-y-3">
                <Label className="text-sm font-semibold">Photos & Documentation</Label>
                <PhotoUpload entityType="inspection" entityId={editingReport.id} photos={reportPhotos} onPhotosChange={setReportPhotos} showTags={true} />
                <div className="border-t border-slate-200 pt-3">
                  <Label className="text-sm font-semibold mb-2 block">Before / After Comparison</Label>
                  <BeforeAfterComparison photos={reportPhotos} />
                </div>
              </div>
            )}
            {!editingReport && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-md border border-amber-200">
                Save the report first, then you can add photos
              </p>
            )}

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={closeModal}>Cancel</Button>
              <Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-inspection-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : (editingReport ? "Update Report" : "Create Report")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
